/**
 * Carries a failed sign-in's server error code from the callback handler to the sign-in page.
 * The API decides every outcome; this binding only keeps the code in sessionStorage for one page
 * load so the sign-in screen can show the matching explanation.
 *
 * @module apps/web/src/lib/signInNotice
 */

/** sessionStorage key holding the last sign-in failure for the sign-in page. */
const STORAGE_KEY = 'fluxability-sign-in-failure';

/** A failed API sign-in exchange, keeping the server's stable error code. */
export class SignInError extends Error {
  /** Stable server code such as `INVITE_ONLY` or `ACCOUNT_EMAIL_IN_USE`. */
  readonly code: string;

  /** Wrap the server's code and user-facing message. */
  constructor(code: string, message: string) {
    super(message);
    this.name = 'SignInError';
    this.code = code;
  }
}

/** The failure shown on the sign-in page. */
export interface SignInFailure {
  /** Server error code, or `SIGN_IN_FAILED` when only a message is known. */
  code: string;
  /** Message to show, as the server wrote it. */
  message: string;
}

/** Remember a callback failure for the sign-in page. Storage errors are ignored. */
export function rememberSignInFailure(error: unknown): void {
  const failure: SignInFailure =
    error instanceof SignInError
      ? { code: error.code, message: error.message }
      : {
          code: 'SIGN_IN_FAILED',
          message: error instanceof Error ? error.message : 'Could not complete sign-in.',
        };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(failure));
  } catch {
    // Private mode or blocked storage: the page falls back to its default text.
  }
}

/** Read the remembered failure, or null. Malformed or missing entries read as null. */
export function readSignInFailure(): SignInFailure | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    return value && typeof value.code === 'string' && typeof value.message === 'string'
      ? { code: value.code, message: value.message }
      : null;
  } catch {
    return null;
  }
}

/** Forget the remembered failure, for example when the person tries again. */
export function clearSignInFailure(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}
